import * as React from 'react'

import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import StarIcon from '@mui/icons-material/Star'
import ShareIcon from '@mui/icons-material/Share'
import PsychologyIcon from '@mui/icons-material/Psychology'
import useMediaQuery from '@mui/material/useMediaQuery'

import {
    useContextsStore,
    findContextByName,
    selectIsPublicView,
} from '../../stores/useContextsStore'
import { useBootstrapStore } from '../../stores/useBootstrapStore'
import { useSnackbar } from '../Snackbar'
import usePageInfo from '../../hooks/usePageInfo'
import {
    isGraphOnlyMode,
    trackEvent,
    emitEvent,
} from '../LeftMenuMigration/legacyBridge'
import ShareDialog from '../ShareDialog'
import BrainDialog from '../BrainDialog'

// Pages where the favorite/share affordances make sense — i.e. anywhere
// the user is looking at a graph (single context, synthesis, or search
// view). Other pages like /apps, /settings, /import don't belong.
const GRAPH_PAGES = ['context', 'conceptsSynthesis', 'synthesis', 'search']

const showSnackbarSelector = s => s.showSnackbar

// Single canonical color shared with the drawer favorite indicator.
const FAVORITE_COLOR = 'orange'

// Shared visual contract for the icons in PageTitle (Favorite, Edit,
// Share) so they line up cleanly. Exported so PageTitle's edit icon
// button can reuse it.
export const HEADER_ICON_SX = {
    p: 0.5,
    '& svg': { fontSize: 18 },
}

const isReadySelector = s => s.isReady

/**
 * Favorite toggle button rendered next to the graph title.
 * Star is always filled; color flips between `text.disabled` (not
 * favorited) and orange (favorited).
 */
export function FavoriteIconButton({ contextName }) {
    const isReady = useBootstrapStore(isReadySelector)
    const isPublic = useContextsStore(selectIsPublicView)
    const { location } = usePageInfo()
    const isFavorite = useContextsStore(state => {
        if (!contextName) return false
        const ctx = findContextByName(state, contextName)
        return ctx ? !!ctx.isFavorite : false
    })
    const setContextFavorite = useContextsStore(s => s.setContextFavorite)
    const showSnackbar = useSnackbar(showSnackbarSelector)
    const [pending, setPending] = React.useState(false)

    const isGraphPage = GRAPH_PAGES.includes(location)
    if (!isReady || !contextName || isPublic || !isGraphPage || isGraphOnlyMode())
        return null

    const onClick = async () => {
        if (pending) return
        setPending(true)
        const next = !isFavorite
        try {
            await setContextFavorite(contextName, next)
            emitEvent('onStatementsAction', {
                action: next ? 'save_to_favorite' : 'remove_from_favorite',
            })
            trackEvent(next ? 'save_to_favorite' : 'remove_from_favorite')
        } catch (e) {
            console.error('[FavoriteIconButton] toggle failed:', e)
            showSnackbar('error', "Couldn't update favorite. Please try again.")
        } finally {
            setPending(false)
        }
    }

    return (
        <Tooltip
            title={isFavorite ? 'Remove from favorites' : 'Save to favorites'}
        >
            <span>
                <IconButton
                    aria-label="favorite-graph-header"
                    size="small"
                    onClick={onClick}
                    disabled={pending}
                    sx={{
                        ...HEADER_ICON_SX,
                        color: isFavorite
                            ? FAVORITE_COLOR
                            : theme => theme.palette.text.disabled,
                        '&:hover': {
                            color: isFavorite ? FAVORITE_COLOR : FAVORITE_COLOR,
                            opacity: 0.85,
                        },
                    }}
                >
                    <StarIcon />
                </IconButton>
            </span>
        </Tooltip>
    )
}

/**
 * Share button rendered next to the graph title. Opens ShareDialog.
 *
 * Highlights with `palette.primary.main` when the current graph is
 * public, so the user can tell at a glance that the graph is shared.
 * Matches the drawer-row public indicator visually.
 */
export function ShareIconButton({ contextName }) {
    const isReady = useBootstrapStore(isReadySelector)
    const { location } = usePageInfo()
    const isPublicContext = useContextsStore(state => {
        if (!contextName) return false
        const ctx = findContextByName(state, contextName)
        return ctx ? !!ctx.isPublic : false
    })
    const [shareOpen, setShareOpen] = React.useState(false)

    const isGraphPage = GRAPH_PAGES.includes(location)
    if (!isReady || !contextName || !isGraphPage || isGraphOnlyMode()) return null

    const onClick = () => {
        setShareOpen(true)
        emitEvent('onStatementsAction', { action: 'open_share_dialog' })
        trackEvent('open_share_dialog')
    }

    return (
        <>
            <Tooltip
                title={
                    isPublicContext
                        ? 'Graph is public. Manage sharing'
                        : 'Share or embed'
                }
            >
                <IconButton
                    aria-label={
                        isPublicContext
                            ? 'share-graph-header-public'
                            : 'share-graph-header'
                    }
                    size="small"
                    onClick={onClick}
                    sx={{
                        ...HEADER_ICON_SX,
                        color: theme =>
                            isPublicContext
                                ? theme.palette.primary.main
                                : theme.palette.text.secondary,
                        '&:hover': {
                            color: theme => theme.palette.primary.main,
                        },
                    }}
                >
                    <ShareIcon />
                </IconButton>
            </Tooltip>
            <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} />
        </>
    )
}

/**
 * Brain button rendered next to the share button. Opens BrainDialog,
 * which explains how to use the current graph as a "brain" for an LLM
 * via the InfraNodus MCP server.
 *
 * Desktop-only: hidden on mobile viewports to keep the header compact.
 */
export function BrainIconButton({ contextName }) {
    const isReady = useBootstrapStore(isReadySelector)
    const { location } = usePageInfo()
    const isMobile = useMediaQuery('(max-width:600px)')
    const [brainOpen, setBrainOpen] = React.useState(false)

    const isGraphPage = GRAPH_PAGES.includes(location)
    if (
        isMobile ||
        !isReady ||
        !contextName ||
        !isGraphPage ||
        isGraphOnlyMode()
    )
        return null

    const onClick = () => {
        setBrainOpen(true)
        emitEvent('onStatementsAction', { action: 'open_brain_dialog' })
        trackEvent('open_brain_dialog')
    }

    return (
        <>
            <Tooltip title="Use this graph as a brain in your LLM">
                <IconButton
                    aria-label="brain-graph-header"
                    size="small"
                    onClick={onClick}
                    sx={{
                        ...HEADER_ICON_SX,
                        color: theme => theme.palette.text.secondary,
                        '&:hover': {
                            color: theme => theme.palette.primary.main,
                        },
                    }}
                >
                    <PsychologyIcon />
                </IconButton>
            </Tooltip>
            <BrainDialog
                open={brainOpen}
                onClose={() => setBrainOpen(false)}
                graphName={contextName}
            />
        </>
    )
}
